import type { SessionUser } from '../../shared/types';

interface GoogleIdTokenPayload {
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  exp?: number;
}

/** Decode a JWT payload (no signature check — the server verifies that). */
export function decodeJwt(token: string): GoogleIdTokenPayload | null {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function initials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return (email[0] || '?').toUpperCase();
}

/** Build a display user from a Google ID token. */
export function userFromToken(token: string): SessionUser | null {
  const p = decodeJwt(token);
  if (!p?.email) return null;
  const name = p.name || p.given_name || p.email;
  return {
    email: p.email,
    name,
    picture: p.picture,
    initials: initials(name, p.email),
  };
}

/** Seconds until the token expires (negative if already expired). */
export function secondsUntilExpiry(token: string): number {
  const p = decodeJwt(token);
  if (!p?.exp) return 0;
  return p.exp - Math.floor(Date.now() / 1000);
}
